import { useState, useMemo, useCallback } from 'react';
import Dropdown from 'components/elements/Dropdown';
import RadioButtons from 'components/elements/RadioButtons';
import ConfirmLocationChangeModal from 'components/widgets/FilterToolbar/filters/RegionSelection/ConfirmLocationChangeModal';
import { useClearRegionSpecificFilters } from 'hooks/filterToolbar/useClearFilters';
import { useResetMapSelections } from 'hooks/projectMap/useResetMapState';
import { useProjectMapQueryParams } from 'hooks/useProjectMap';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useCurrentlySelectedCities } from 'state/currentLocation';
import { useCurrentRegion } from 'state/currentRegion';
import { useUserMultiRegionSelection, useUserStateSelection } from 'state/dashboard';
import { useClearAllState } from 'state/filterMapPins';
import { useMapProperties, useMapState } from 'state/map';
import { DEFAULT_MAP_ZOOM } from 'utils/constants';
import { Region, State, STATE_CODE_TO_LABEL } from 'utils/constants/location';

type Props = {
  onLocationChangeCallback?: () => void;
};

const StateSelection = ({ onLocationChangeCallback }: Props) => {
  const [selectedState, setSelectedState] = useUserStateSelection();
  const [targetState, setTargetState] = useState<string | undefined>();
  const { availableStates } = useAvailableCities();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { setCurrentRegion } = useCurrentRegion();
  const { setCurrentlySelectedCities } = useCurrentlySelectedCities();
  const { updateSelectedPolygons } = useMapState();
  const { clearRegionSpecificFilters } = useClearRegionSpecificFilters();
  const { resetAllQueryParamsForProjectMap } = useProjectMapQueryParams();
  const { resetAllMapSelections } = useResetMapSelections();
  const { clearAllMapPinsAndClusters } = useClearAllState();
  const { updateMapZoom } = useMapProperties();
  const [, setSelectedRegions] = useUserMultiRegionSelection();

  const stateOptions = useMemo(() => {
    return Object.keys(availableStates).map((key: string) => {
      return {
        value: key,
        label: STATE_CODE_TO_LABEL[key] || key,
      };
    });
  }, [availableStates]);

  const handleStateChange = useCallback(
    (value: string) => {
      // Only show confirmation modal if state is actually changing
      if (value !== selectedState) {
        setTargetState(value);
        setIsConfirmModalOpen(true);
      }
    },
    [selectedState, setTargetState, setIsConfirmModalOpen],
  );

  const closeConfirmationModal = useCallback(() => {
    setIsConfirmModalOpen(false);
    setTargetState(undefined);
  }, [setIsConfirmModalOpen, setTargetState]);

  const handleConfirmLocationChange = useCallback(() => {
    if (!targetState) {
      closeConfirmationModal();

      return;
    }

    // Some filters are city specific. Clear them when switching cities.
    clearRegionSpecificFilters();
    resetAllQueryParamsForProjectMap();
    updateSelectedPolygons({});
    resetAllMapSelections();
    clearAllMapPinsAndClusters();
    setCurrentlySelectedCities([]);
    updateMapZoom(DEFAULT_MAP_ZOOM);
    const allRegionsForState = Object.keys(availableStates[targetState].regions);
    setSelectedRegions(allRegionsForState as Region[]);
    setCurrentRegion(Region.US_AUSTIN);

    setSelectedState(targetState as State);

    if (onLocationChangeCallback) {
      onLocationChangeCallback();
    }
  }, [
    targetState,
    setSelectedState,
    clearRegionSpecificFilters,
    resetAllQueryParamsForProjectMap,
    updateSelectedPolygons,
    resetAllMapSelections,
    clearAllMapPinsAndClusters,
    setCurrentlySelectedCities,
    updateMapZoom,
    availableStates,
    setSelectedRegions,
    setCurrentRegion,
    onLocationChangeCallback,
    closeConfirmationModal,
  ]);

  const indicatorText = useMemo(() => {
    if (!selectedState) {
      return '';
    }

    return STATE_CODE_TO_LABEL[selectedState]?.toUpperCase() || '';
  }, [selectedState]);

  return (
    <>
      <Dropdown title="State" filterCount={indicatorText}>
        <RadioButtons
          value={selectedState || ''}
          onChange={handleStateChange}
          options={stateOptions}
        />
      </Dropdown>
      <ConfirmLocationChangeModal
        isOpen={isConfirmModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmLocationChange}
      />
    </>
  );
};

const StateSelectionWrapper = (props: Props) => {
  const [selectedState] = useUserStateSelection();

  return <StateSelection key={`state-selector-${selectedState}`} {...props} />;
};

export default StateSelectionWrapper;
